import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Input, Select, InputNumber } from "antd";
import { SearchBar, FaqAccordion, useToast } from "../../components";
import { user as userActions } from "../../store";
import "./account.css";

const genderOptions = [
  { label: "Male", value: "MALE" },
  { label: "Female", value: "FEMALE" },
];

const validateField = (field, value) => {
  if (field === "firstName") {
    if (value.length > 0 && value.length < 2)
      return "First name must be between 2 and 50 characters";
    if (value.length > 50)
      return "First name must be between 2 and 50 characters";
    if (value.length > 0 && !/^[a-zA-Z\s]+$/.test(value))
      return "First name can only contain letters and spaces";
    return "";
  }

  if (field === "lastName") {
    if (value.length > 50)
      return "Last name must be between 1 and 50 characters";
    if (value.length > 0 && !/^[a-zA-Z\s]+$/.test(value))
      return "Last name can only contain letters and spaces";
    return "";
  }

  if (field === "age") {
    if (value === null || value === undefined || value === "") return "";
    if (!Number.isInteger(value) || !Number.isFinite(value))
      return "Age must be a number between 1 and 150";
    if (value < 1 || value > 150)
      return "Age must be a number between 1 and 150";
    return "";
  }

  if (field === "gender") {
    if (!value) return "";
    if (value !== "MALE" && value !== "FEMALE")
      return "Gender must be MALE or FEMALE";
    return "";
  }

  return "";
};

const Account = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const toast = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    age: null,
    gender: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    age: "",
    gender: "",
  });

  // Fetch latest profile data when component mounts
  useEffect(() => {
    dispatch(userActions.profileApi());
  }, [dispatch]);

  useEffect(() => {
    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      age: user.age !== null && user.age !== undefined ? user.age : null,
      gender: user.gender || "",
      email: user.email || "",
      phone: user.phone || "",
    });
    setErrors({ firstName: "", lastName: "", age: "", gender: "" });
  }, [
    user.firstName,
    user.lastName,
    user.age,
    user.gender,
    user.email,
    user.phone,
  ]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Validate the changed field in real-time
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel: reset form to original user data
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        age: user.age !== null && user.age !== undefined ? user.age : null,
        gender: user.gender || "",
        email: user.email || "",
        phone: user.phone || "",
      });
      setErrors({ firstName: "", lastName: "", age: "", gender: "" });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    // Validate all fields before saving
    const newErrors = {
      firstName: validateField("firstName", formData.firstName),
      lastName: validateField("lastName", formData.lastName),
      age: validateField("age", formData.age),
      gender: validateField("gender", formData.gender),
    };
    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((e) => e);
    if (hasErrors) return;

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      age:
        formData.age !== null && formData.age !== undefined
          ? Number(formData.age)
          : null,
      gender: formData.gender,
    };

    try {
      await dispatch(userActions.updateProfileApi(payload)).unwrap();
      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (error) {
      toast.error(error || "Failed to update profile. Please try again.");
    }
  };

  const fullName =
    user.fullName ||
    [formData.firstName, formData.lastName].filter(Boolean).join(" ") ||
    "User";

  // Compute whether form values differ from original user data
  const hasChanges = useMemo(() => {
    const originalFirstName = user.firstName || "";
    const originalLastName = user.lastName || "";
    const originalAge =
      user.age !== null && user.age !== undefined ? user.age : null;
    const originalGender = user.gender || "";
    const originalEmail = user.email || "";
    const originalPhone = user.phone || "";

    return (
      formData.firstName !== originalFirstName ||
      formData.lastName !== originalLastName ||
      String(formData.age ?? "") !== String(originalAge ?? "") ||
      formData.gender !== originalGender ||
      formData.email !== originalEmail ||
      formData.phone !== originalPhone
    );
  }, [formData, user]);

  // Compute whether any validation errors exist
  const hasValidationErrors = useMemo(() => {
    return Object.values(errors).some((e) => e !== "");
  }, [errors]);

  const canSave = hasChanges && !hasValidationErrors;

  const displayValue = (field) => {
    const val = formData[field];
    if (field === "age" && (val === null || val === "")) return "—";
    if (field === "gender" && !val) return "—";
    if (field === "phone" && !val) return "—";
    return val;
  };

  return (
    <div>
      <SearchBar />

      <div className="account-container">
        <div className="account-header-bar">
          <div className="account-header-text">
            <h1>Account Information</h1>
            <p className="account-subtitle">
              Manage your personal details and coaching profile.
            </p>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* Left Column: Profile Identity Card */}
          <Col xs={24} md={8}>
            <div className="profile-identity-card">
              <div className="profile-glow" />
              <div className="profile-avatar-wrapper">
                <img
                  src={
                    user.picture
                      ? `${process.env.REACT_APP_API_URL || ""}${user.picture}`
                      : "/default-avatar.png"
                  }
                  alt={fullName}
                  className="profile-avatar"
                  onError={(e) => {
                    e.target.src = "/default-avatar.png";
                  }}
                />
                {isEditing && (
                  <button className="update-photo-btn" type="button">
                    <span className="material-symbols-outlined">
                      photo_camera
                    </span>
                  </button>
                )}
              </div>
              <h2 className="profile-name">{fullName}</h2>
              {!isEditing && (
                <button
                  className="edit-profile-btn"
                  onClick={handleEditToggle}
                  type="button"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.8333 1.66675C11.0544 1.44574 11.3149 1.26761 11.6014 1.14124C11.8879 1.01487 12.1958 0.950012 12.505 0.950012C12.8141 0.950012 13.122 1.01487 13.4085 1.14124C13.695 1.26761 13.9555 1.44574 14.1667 1.66675C14.3877 1.88776 14.5658 2.14829 14.6922 2.43479C14.8185 2.72129 14.8834 3.02919 14.8834 3.33833C14.8834 3.64747 14.8185 3.95537 14.6922 4.24187C14.5658 4.52837 14.3877 4.7889 14.1667 5.01008L5.41667 13.7592L1.66667 14.5834L2.49083 10.8334L10.8333 1.66675Z"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Edit Profile
                </button>
              )}
            </div>
          </Col>

          {/* Right Column: Personal Details */}
          <Col xs={24} md={16}>
            <div className="personal-details-card">
              <div className="personal-details-header">
                <div className="personal-details-header-left">
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: "18px",
                      color: "rgba(8, 148, 155, 0.8)",
                    }}
                  >
                    person
                  </span>
                  <h2>Personal Details</h2>
                </div>
                <span className="verified-badge">
                  <span className="material-symbols-outlined">verified</span>
                  VERIFIED
                </span>
              </div>

              <div className="details-fields">
                <div className="fields-grid">
                  {/* First Name */}
                  <div className="field-group">
                    <label className="field-label">
                      First Name <span className="required-asterisk">*</span>
                    </label>
                    {isEditing ? (
                      <>
                        <div
                          className={`field-value-row${
                            errors.firstName ? " has-error" : ""
                          }`}
                        >
                          <Input
                            value={formData.firstName}
                            onChange={(e) =>
                              handleInputChange("firstName", e.target.value)
                            }
                            placeholder="Enter first name"
                            bordered={false}
                            className="ant-input-custom"
                          />
                        </div>
                        <div className="field-error">{errors.firstName}</div>
                      </>
                    ) : (
                      <div className="field-value">
                        {displayValue("firstName")}
                      </div>
                    )}
                  </div>

                  {/* Last Name */}
                  <div className="field-group">
                    <label className="field-label">Last Name</label>
                    {isEditing ? (
                      <>
                        <div
                          className={`field-value-row${
                            errors.lastName ? " has-error" : ""
                          }`}
                        >
                          <Input
                            value={formData.lastName}
                            onChange={(e) =>
                              handleInputChange("lastName", e.target.value)
                            }
                            placeholder="Enter last name"
                            bordered={false}
                            className="ant-input-custom"
                          />
                        </div>
                        <div className="field-error">{errors.lastName}</div>
                      </>
                    ) : (
                      <div className="field-value">
                        {displayValue("lastName")}
                      </div>
                    )}
                  </div>

                  {/* Age */}
                  <div className="field-group">
                    <label className="field-label">
                      Age <span className="required-asterisk">*</span>
                    </label>
                    {isEditing ? (
                      <>
                        <div
                          className={`field-value-row${
                            errors.age ? " has-error" : ""
                          }`}
                        >
                          <InputNumber
                            value={formData.age}
                            onChange={(value) =>
                              handleInputChange("age", value)
                            }
                            placeholder="Enter age"
                            min={1}
                            max={150}
                            precision={0}
                            step={1}
                            bordered={false}
                            className="ant-input-custom"
                            style={{ width: "100%" }}
                          />
                        </div>
                        <div className="field-error">{errors.age}</div>
                      </>
                    ) : (
                      <div className="field-value">{displayValue("age")}</div>
                    )}
                  </div>

                  {/* Gender */}
                  <div className="field-group">
                    <label className="field-label">
                      Gender <span className="required-asterisk">*</span>
                    </label>
                    {isEditing ? (
                      <>
                        <div
                          className={`field-value-row${
                            errors.gender ? " has-error" : ""
                          }`}
                        >
                          <Select
                            value={formData.gender || undefined}
                            onChange={(value) =>
                              handleInputChange("gender", value)
                            }
                            placeholder="Select gender"
                            bordered={false}
                            className="ant-select-custom"
                            style={{ width: "100%" }}
                            options={genderOptions}
                          />
                        </div>
                        <div className="field-error">{errors.gender}</div>
                      </>
                    ) : (
                      <div className="field-value">
                        {displayValue("gender")}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div className="field-group">
                    <label className="field-label">Email</label>
                    <div className="field-value">{formData.email || "—"}</div>
                  </div>

                  {/* Phone */}
                  <div className="field-group">
                    <label className="field-label">Phone</label>
                    <div className="field-value">{formData.phone || "—"}</div>
                  </div>
                </div>

                {/* Edit Mode Buttons */}
                {isEditing && (
                  <div className="edit-actions-section">
                    <div className="edit-actions-row">
                      <button
                        className="edit-profile-btn cancel-btn"
                        onClick={handleEditToggle}
                        type="button"
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.25 3.75L3.75 11.25"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3.75 3.75L11.25 11.25"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Cancel
                      </button>
                      <button
                        className={`edit-profile-btn save-btn${
                          !canSave ? " disabled" : ""
                        }`}
                        onClick={handleSave}
                        type="button"
                        disabled={!canSave}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.5 9.16667V11.6667C12.5 12.1269 12.1269 12.5 11.6667 12.5H3.33333C2.8731 12.5 2.5 12.1269 2.5 11.6667V3.33333C2.5 2.8731 2.8731 2.5 3.33333 2.5H5.83333"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.8333 2.5L13.3333 5M7.08333 7.91667C7.54357 7.91667 7.91667 7.54357 7.91667 7.08333C7.91667 6.6231 7.54357 6.25 7.08333 6.25C6.6231 6.25 6.25 6.6231 6.25 7.08333C6.25 7.54357 6.6231 7.91667 7.08333 7.91667Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}

                {/* Account Deletion */}
                {/* <div className="account-deletion-section">
                  <button className="request-deletion-btn" type="button">
                    Request Account Deletion
                  </button>
                </div> */}
              </div>
            </div>
          </Col>
        </Row>

        <FaqAccordion variant="account" />
      </div>
    </div>
  );
};

export default Account;
